'use client';

import { OrderStatusDefinitionIdentifier } from '@order-mage/integration-sdk';
import { cva, VariantProps } from 'class-variance-authority';
import { ComponentPropsWithRef, ElementType, useMemo } from 'react';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';
import type { IconName } from '@/shared/ui/icon';
import './order-progress.scss';

const orderProgressVariants = cva('order-progress', {
    variants: {
        state: {
            awaitingPayment: 'order-progress--awaiting-payment',
            created: 'order-progress--created',
            accepted: 'order-progress--accepted',
            acceptedTimed: 'order-progress--accepted-timed',
            cooking: 'order-progress--cooking',
            cooked: 'order-progress--cooked',
            delivering: 'order-progress--delivering',
            review: 'order-progress--review',
            canceled: 'order-progress--canceled',
        },
        size: {
            normal: 'order-progress--normal',
            big: 'order-progress--big',
        },
    },
    defaultVariants: {
        state: 'created',
        size: 'normal',
    },
});

export type OrderProgressState = VariantProps<typeof orderProgressVariants>['state'];

// TODO: Add the rest of new statuses
export const orderStateMap: Record<OrderStatusDefinitionIdentifier, OrderProgressState> = {
    NEW: 'created',
    ACCEPTED: 'accepted',
    PREPARING: 'cooking',
    PREPARED: 'cooked',
    IN_DELIVERY: 'delivering',
    DONE: 'review',
    CANCELLED: 'canceled',
    POSTPONED: 'acceptedTimed',
    NOT_DELIVERED: 'canceled',
    AWAITING_PAYMENT: 'awaitingPayment',
};

export const getOrderStatusIcon = (state: OrderProgressState): IconName => {
    switch (state) {
        case 'awaitingPayment':
            return 'credit-card';
        case 'accepted':
        case 'acceptedTimed':
            return 'success';
        case 'cooking':
            return 'chef-hat';
        case 'cooked':
            return 'bag';
        case 'delivering':
            return 'truck-fast';
        case 'review':
            return 'star-fill';
        case 'canceled':
            return 'close';
        case 'created':
        default:
            return 'loading';
    }
};

type Props<T extends ElementType = 'button'> = {
    as?: T;
    count?: number;
} & ComponentPropsWithRef<'button'> &
    VariantProps<typeof orderProgressVariants>;
export const OrderProgress = <T extends ElementType = 'button'>({
    as,
    state = 'created',
    size,
    count,
    className,
    ...props
}: Props<T>) => {
    const Element = as ?? 'button';
    const iconName = useMemo(() => getOrderStatusIcon(state), [state]);
    const progress = useMemo(() => {
        switch (state) {
            case 'awaitingPayment':
                return 10;
            case 'accepted':
            case 'acceptedTimed':
                return 33;
            case 'cooking':
                return 50;
            case 'cooked':
                return 67;
            case 'delivering':
                return 83;
            case 'review':
            case 'canceled':
                return 100;
            case 'created':
            default:
                return 17;
        }
    }, [state]);

    return (
        <Element
            data-testid="order-progress-button"
            data-state={state}
            {...props}
            className={orderProgressVariants({ state, size, className })}
        >
            <div
                className="order-progress__progress"
                style={{
                    '--progress': progress,
                }}
            >
                <div className="order-progress__inner">
                    <Icon
                        name={iconName}
                        className={cn(
                            'order-progress__icon',
                            state === 'created' && 'order-progress__icon--rotate',
                        )}
                        data-testid="order-progress-icon"
                    />
                    {count && (
                        <span className="order-progress__badge" data-testid="order-progress-badge">
                            {count}
                        </span>
                    )}
                </div>
            </div>
        </Element>
    );
};
